/*import React, { useState, useEffect } from 'react';
import { db } from '../serices/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { Trash2, Plus, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SponsorManager = () => {
  const [sponsors, setSponsors] = useState([]);
  const [name, setName] = useState('');
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  // FETCH SPONSORS
  const fetchSponsors = async () => {
    const q = query(collection(db, "sponsors"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    setSponsors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchSponsors(); }, []);

  // UPLOAD LOGIC
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!image || !name) return alert("Please provide both a name and a logo.");
    setLoading(true);

    try {
      const data = new FormData();
      data.append("file", image);
      data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
      
      const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { 
        method: "POST", 
        body: data 
      });
      const fileData = await res.json();

      if (!res.ok) throw new Error(fileData.error?.message || "Upload failed");

      await addDoc(collection(db, "sponsors"), {
        name: name.trim(),
        logoUrl: fileData.secure_url,
        createdAt: serverTimestamp()
      });

      setName('');
      setImage(null);
      fetchSponsors();
    } catch (err) {
      alert("Error: " + err.message);
    } finally { setLoading(false); }
  };

  const deleteSponsor = async (id) => {
    if(window.confirm("Remove this sponsor?")) {
        await deleteDoc(doc(db, "sponsors", id));
        fetchSponsors();
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* --- UPLOAD SECTION --- *//*
      <div className="bg-zinc-900/40 border border-white/5 p-8 rounded-[2.5rem] backdrop-blur-md">
        <h3 className="text-[#D4AF37] text-[10px] font-black uppercase tracking-[0.4em] mb-8 italic flex items-center gap-3">
            <Plus size={14}/> Add New Partner
        </h3>
        
        <form onSubmit={handleUpload} className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* 1. IMAGE PREVIEW / UPLOAD BOX *//*
          <div className="w-full lg:w-48 h-48 relative group">
            {image ? (
              <div className="w-full h-full rounded-2xl overflow-hidden border-2 border-[#D4AF37] relative">
                <img 
                  src={URL.createObjectURL(image)} 
                  alt="Preview" 
                  className="w-full h-full object-contain bg-black p-4" 
                />
                <button 
                  type="button"
                  onClick={() => setImage(null)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:scale-110 transition-transform"
                >
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <div className="w-full h-full rounded-2xl border-2 border-dashed border-white/10 hover:border-[#D4AF37]/50 transition-all flex flex-col items-center justify-center bg-black/40 relative cursor-pointer group">
                <ImageIcon size={32} className="text-white/10 group-hover:text-[#D4AF37] transition-colors mb-2" />
                <p className="text-[8px] font-black uppercase text-white/20 tracking-widest">Select Logo</p>
                <input 
                  type="file" 
                  required
                  accept="image/*"
                  onChange={e => setImage(e.target.files[0])} 
                  className="absolute inset-0 opacity-0 cursor-pointer" 
                />
              </div>
            )}
          </div>

          {/* 2. FIELDS *//*
          <div className="flex-1 space-y-6 w-full">
            <div>
              <label className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em] ml-1 mb-2 block">Partner / Sponsor Name</label>
              <input 
                type="text" 
                required
                value={name} 
                onChange={e => setName(e.target.value)} 
                className="w-full bg-black/50 border border-white/10 p-5 rounded-2xl outline-none focus:border-[#D4AF37] text-white font-bold transition-all placeholder:text-white/5" 
                placeholder="e.g. Voi Heights Hotel" 
              />
            </div>
            
            <button 
              disabled={loading || !image || !name} 
              className="w-full bg-[#D4AF37] text-black font-black py-5 rounded-2xl flex items-center justify-center gap-3 hover:bg-white transition-all disabled:opacity-30 shadow-2xl text-xs tracking-[0.3em] uppercase italic"
            >
              {loading ? <Loader2 className="animate-spin" size={20}/> : <Plus size={20}/>} 
              Confirm & Add Partner
            </button>
          </div>
        </form>
      </div>

      {/* --- GRID DISPLAY --- *//*
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <AnimatePresence>
            {sponsors.map(s => (
            <motion.div 
                key={s.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="group relative bg-zinc-900/20 border border-white/5 p-6 rounded-3xl flex flex-col items-center justify-center hover:border-[#D4AF37]/30 transition-all aspect-square"
            >
                <img src={s.logoUrl} alt={s.name} className="h-12 w-full object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-500" />
                <p className="text-[8px] font-black uppercase tracking-widest text-white/30 text-center truncate w-full">{s.name}</p>
                
                <button 
                    onClick={() => deleteSponsor(s.id)} 
                    className="absolute top-3 right-3 bg-red-600/20 text-red-500 p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 hover:text-white"
                >
                <Trash2 size={14} />
                </button>
            </motion.div>
            ))}
        </AnimatePresence>
      </div>

      {sponsors.length === 0 && !loading && (
        <div className="text-center py-20 border border-dashed border-white/5 rounded-[2.5rem]">
           <p className="text-white/10 uppercase font-black tracking-[0.5em] text-[9px] italic">No active partnerships found</p>
        </div>
      )}
    </motion.div>
  );
};

export default SponsorManager;*/




import React, { useState, useEffect } from 'react';
import { db } from '../serices/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { Trash2, Plus, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SponsorManager = () => {
  const [sponsors, setSponsors] = useState([]);
  const [name, setName] = useState('');
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  // FETCH SPONSORS
  const fetchSponsors = async () => {
    const q = query(collection(db, "sponsors"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    setSponsors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchSponsors(); }, []);

  // Convert the selected logo file to base64 for our upload function
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]); // strip the "data:...;base64," prefix
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // UPLOAD LOGIC
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!image || !name) return alert("Please provide both a name and a logo.");
    setLoading(true);

    try {
      // --- Bunny.net Upload (via secure serverless function) ---
      // Same pattern as the nomination photo upload: the file is base64-encoded
      // here and forwarded to our Netlify function, which holds the Bunny
      // Storage AccessKey server-side and returns just the public CDN URL.
      const fileBase64 = await fileToBase64(image);

      const res = await fetch('/.netlify/functions/uploadImage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileBase64,
          fileName: image.name,
          contentType: image.type
        })
      });
      const fileData = await res.json();

      if (!res.ok) throw new Error(fileData.error || "Upload failed");

      await addDoc(collection(db, "sponsors"), {
        name: name.trim(),
        logoUrl: fileData.imageUrl,
        createdAt: serverTimestamp()
      });

      setName('');
      setImage(null);
      fetchSponsors();
    } catch (err) {
      alert("Error: " + err.message);
    } finally { setLoading(false); }
  };

  const deleteSponsor = async (id) => {
    if(window.confirm("Remove this sponsor?")) {
        await deleteDoc(doc(db, "sponsors", id));
        fetchSponsors();
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* --- UPLOAD SECTION --- */}
      <div className="bg-zinc-900/40 border border-white/5 p-8 rounded-[2.5rem] backdrop-blur-md">
        <h3 className="text-[#D4AF37] text-[10px] font-black uppercase tracking-[0.4em] mb-8 italic flex items-center gap-3">
            <Plus size={14}/> Add New Partner
        </h3>
        
        <form onSubmit={handleUpload} className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* 1. IMAGE PREVIEW / UPLOAD BOX */}
          <div className="w-full lg:w-48 h-48 relative group">
            {image ? (
              <div className="w-full h-full rounded-2xl overflow-hidden border-2 border-[#D4AF37] relative">
                <img 
                  src={URL.createObjectURL(image)} 
                  alt="Preview" 
                  className="w-full h-full object-contain bg-black p-4" 
                />
                <button 
                  type="button"
                  onClick={() => setImage(null)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:scale-110 transition-transform"
                >
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <div className="w-full h-full rounded-2xl border-2 border-dashed border-white/10 hover:border-[#D4AF37]/50 transition-all flex flex-col items-center justify-center bg-black/40 relative cursor-pointer group">
                <ImageIcon size={32} className="text-white/10 group-hover:text-[#D4AF37] transition-colors mb-2" />
                <p className="text-[8px] font-black uppercase text-white/20 tracking-widest">Select Logo</p>
                <input 
                  type="file" 
                  required
                  accept="image/*"
                  onChange={e => setImage(e.target.files[0])} 
                  className="absolute inset-0 opacity-0 cursor-pointer" 
                />
              </div>
            )}
          </div>

          {/* 2. FIELDS */}
          <div className="flex-1 space-y-6 w-full">
            <div>
              <label className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em] ml-1 mb-2 block">Partner / Sponsor Name</label>
              <input 
                type="text" 
                required
                value={name} 
                onChange={e => setName(e.target.value)} 
                className="w-full bg-black/50 border border-white/10 p-5 rounded-2xl outline-none focus:border-[#D4AF37] text-white font-bold transition-all placeholder:text-white/5" 
                placeholder="e.g. Voi Heights Hotel" 
              />
            </div>
            
            <button 
              disabled={loading || !image || !name} 
              className="w-full bg-[#D4AF37] text-black font-black py-5 rounded-2xl flex items-center justify-center gap-3 hover:bg-white transition-all disabled:opacity-30 shadow-2xl text-xs tracking-[0.3em] uppercase italic"
            >
              {loading ? <Loader2 className="animate-spin" size={20}/> : <Plus size={20}/>} 
              Confirm & Add Partner
            </button>
          </div>
        </form>
      </div>

      {/* --- GRID DISPLAY --- */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <AnimatePresence>
            {sponsors.map(s => (
            <motion.div 
                key={s.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="group relative bg-zinc-900/20 border border-white/5 p-6 rounded-3xl flex flex-col items-center justify-center hover:border-[#D4AF37]/30 transition-all aspect-square"
            >
                <img src={s.logoUrl} alt={s.name} className="h-12 w-full object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-500" />
                <p className="text-[8px] font-black uppercase tracking-widest text-white/30 text-center truncate w-full">{s.name}</p>
                
                <button 
                    onClick={() => deleteSponsor(s.id)} 
                    className="absolute top-3 right-3 bg-red-600/20 text-red-500 p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 hover:text-white"
                >
                <Trash2 size={14} />
                </button>
            </motion.div>
            ))}
        </AnimatePresence>
      </div>

      {sponsors.length === 0 && !loading && (
        <div className="text-center py-20 border border-dashed border-white/5 rounded-[2.5rem]">
           <p className="text-white/10 uppercase font-black tracking-[0.5em] text-[9px] italic">No active partnerships found</p>
        </div>
      )}
    </motion.div>
  );
};

export default SponsorManager;
